// src/auth/storage.ts
import type { UserData } from './types';

const KEYS = {
  access: 'scrips.access_token',
  refresh: 'scrips.refresh_token',
  user: 'scrips.user',
  userVersion: 'scrips.user_v',
} as const;

/**
 * Persisted-profile schema version. BUMP whenever a newly-read `GetUserDataToken` field starts
 * gating behaviour, so a profile cached by an older build (which lacks that field) is refetched on
 * boot instead of being trusted. A stale cache that silently omits a gate field can flip a check the
 * wrong way — e.g. a missing `medicationSystem` reads as Vidal-compatible and shows a false-green
 * drug-interaction confirmation for a non-Vidal org (see useIsDrugInteractionEnabled).
 *  - v2: added `medicationSystem` (drug-interaction Vidal gate).
 *  - v3: added `resourceBasedIntegration` (the Malaffi HIE SSO tab gate). A stale cache omitting
 *        it fails CLOSED — the tab silently disappears for a Malaffi org, so the doctor loses the
 *        external-records surface. The refetch is what keeps that from happening after a deploy.
 */
export const USER_DATA_SCHEMA_VERSION = 3;

export const authStorage = {
  getAccessToken: (): string | null => localStorage.getItem(KEYS.access),
  setAccessToken: (t: string): void => localStorage.setItem(KEYS.access, t),

  getRefreshToken: (): string | null => localStorage.getItem(KEYS.refresh),
  setRefreshToken: (t: string): void => localStorage.setItem(KEYS.refresh, t),

  getUser: (): UserData | null => {
    const raw = localStorage.getItem(KEYS.user);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as UserData;
    } catch {
      return null;
    }
  },
  setUser: (u: UserData): void => {
    localStorage.setItem(KEYS.user, JSON.stringify(u));
    // Stamp the schema version alongside the profile — a freshly-fetched profile is always current.
    localStorage.setItem(KEYS.userVersion, String(USER_DATA_SCHEMA_VERSION));
  },

  /**
   * True when the persisted profile predates the current schema (older build, or none stamped).
   * Callers should refetch `GetUserDataToken` before trusting version-gated fields.
   */
  isUserSchemaStale: (): boolean => {
    const raw = localStorage.getItem(KEYS.userVersion);
    const v = raw != null ? Number.parseInt(raw, 10) : 0;
    return !Number.isFinite(v) || v < USER_DATA_SCHEMA_VERSION;
  },

  clear: (): void => {
    localStorage.removeItem(KEYS.access);
    localStorage.removeItem(KEYS.refresh);
    localStorage.removeItem(KEYS.user);
    localStorage.removeItem(KEYS.userVersion);
  },
};
